import React, { useState, useEffect, useRef } from 'react';
import { Select, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  LoadingOutlined, CloseOutlined, UpOutlined, MinusOutlined,
  ExpandOutlined, CompressOutlined, EditOutlined, SnippetsOutlined,
  PaperClipOutlined, SaveOutlined, SendOutlined,
} from '@ant-design/icons';
import axiosInstance from '../../variables/api/axiosInstance';
import { getUser } from '../../variables/constants';
import T from './theme';
import OssanEditor from './OssanEditor';
import { signatureAvecLogo, urlLogoOrganisme } from '../../utils/signatureCourriel';

import i18n from '../../i18n';
import { libellesTraduits } from '../../i18n/libelles';

const NATURE_OPTIONS = [
  'RELANCE', 'DEMANDE_PIECES', 'DEMANDE_OFFRE', 'TRANSMISSION_EXPERTISE',
  'CONFIRMATION_GARANTIE', 'ACCORD_VICTIME', 'BAP', 'TRANSMISSION_PV',
  'OFFRE_INDEMNISATION', 'COMPTE_RENDU_AUDIENCE', 'AUTRE',
];
const NATURE_LABEL = libellesTraduits('messagerie:natures');

function ToChip({ email, name, onRemove }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      background: T.greenSoft, color: T.green, borderRadius: 4,
      padding: '2px 8px', fontSize: 12.5, fontWeight: 500,
    }}>
      {name || email}
      <button
        onClick={onRemove}
        style={{ border: 'none', background: 'transparent', color: '#6b7280', cursor: 'pointer', padding: 0, fontSize: 12, display: 'flex', alignItems: 'center' }}
      >
        <CloseOutlined />
      </button>
    </span>
  );
}

export default function ComposeWindow({ win, onUpdate, onClose, onSend, onDraft, templates, sinistres, openIndex, minIndex }) {
  const { t } = useTranslation('messagerie');
  const [toInput, setToInput] = useState('');
  const [tab, setTab] = useState('libre');
  const [templateId, setTemplateId] = useState(null);
  const [tplPreview, setTplPreview] = useState('');
  const [tplBody, setTplBody] = useState(''); // modèle résolu + éditable
  const tplDirty = useRef(false);
  const [loadingTpl, setLoadingTpl] = useState(false);
  const [sending, setSending] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [pos, setPos] = useState(null);
  const [signature, setSignature] = useState('');
  /* Le logo du bureau, place a gauche de la signature. Charge une fois : son
     adresse ne suffit pas a savoir s'il existe - un organisme sans logo repondrait
     404, et le message partirait avec un cadre vide. */
  const [logoSignature, setLogoSignature] = useState(null);
  const [beneficiaires, setBeneficiaires] = useState('');

  // Jetons de contexte utilisateur non résolus par le moteur de template
  // (le backend rend les jetons liés au sinistre, mais pas ceux-ci).
  const user = getUser();
  const expediteurNom =
    [user?.prenoms || user?.prenom, user?.nom].filter(Boolean).join(' ').trim()
    || user?.email || '';

  useEffect(() => {
    axiosInstance.get('/v1/messagerie/signature')
      .then(r => setSignature(r.data?.data || ''))
      .catch(() => { });
  }, []);

  useEffect(() => {
    const org = user?.organismeTrackingId;
    if (!org) return;
    axiosInstance.get(`/v1/organismes/${org}`, { skipErrorToast: true })
      .then(r => setLogoSignature(urlLogoOrganisme(org, r.data?.data?.logo)))
      // Sans logo, la signature part seule : c'est exactement ce qu'elle faisait avant.
      .catch(() => setLogoSignature(null));
  }, [user?.organismeTrackingId]);

  // Bénéficiaires du sinistre lié = ses victimes (un sinistre peut en avoir plusieurs).
  useEffect(() => {
    const stid = win.sinistreId;
    if (!stid) { setBeneficiaires(''); return; }
    // skipErrorToast : un profil sans VICTIME_READ (ex. COMPTABLE, SECRETAIRE) reçoit un 403 ici ;
    // le .catch juste en dessous vide déjà la liste, un bandeau d'erreur n'apprendrait rien à l'agent.
    axiosInstance.get(`/v1/victimes/sinistre/${stid}`, { skipErrorToast: true })
      .then(r => {
        const arr = r.data?.data?.content || r.data?.data || [];
        const noms = (Array.isArray(arr) ? arr : [])
          .map(v => `${v.nom || ''} ${v.prenoms || v.prenom || ''}`.trim())
          .filter(Boolean);
        setBeneficiaires(noms.join(', '));
      })
      .catch(() => setBeneficiaires(''));
  }, [win.sinistreId]);

  // Texte brut normalisé (sans balises) pour comparer des contenus HTML.
  const stripText = s => (s || '')
    .replace(/<[^>]+>/g, ' ').replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ').trim();

  // Remplace {{expediteur_nom}} et {{signature}} par les valeurs réelles.
  // Replacer en fonction pour ne pas interpréter les $ éventuels du HTML.
  // Anti-duplication : si la signature figure déjà dans le corps
  // (template la contenant déjà), le jeton {{signature}} est vidé.
  const resolve = html => {
    let out = (html || '')
      .replace(/\{\{\s*expediteur_nom\s*\}\}/g, () => expediteurNom)
      .replace(/\{\{\s*b[eé]n[eé]ficiaires?\s*\}\}/gi, () => beneficiaires);
    const sigText = stripText(signature);
    const bodySansToken = stripText(out.replace(/\{\{\s*signature\s*\}\}/g, ''));
    const dejaPresente = sigText.length > 0 && bodySansToken.includes(sigText);
    // Le logo est ajoute ICI et non dans la signature enregistree : celle-ci reste
    // le texte que l'agent a ecrit, et le logo suit l'organisme, pas la saisie.
    const sigRendue = signatureAvecLogo(signature, logoSignature);
    out = out.replace(/\{\{\s*signature\s*\}\}/g, () => (dejaPresente ? '' : sigRendue));
    return out;
  };

  // ── Variables verrouillées dans l'éditeur de modèle ──────────────
  // Le texte reste adaptable, mais les variables (valeurs issues des données)
  // sont rendues non éditables (spans/divs contenteditable=false).
  const escapeHtml = s => (s || '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const varChip = inner =>
    `<span class="msg-var" contenteditable="false" style="background:${T.accentSoft};` +
    `color:#065f46;border-radius:4px;padding:0 3px;">${inner}</span>`;

  const seedTemplate = html => {
    let out = (html || '')
      .replace(/\{\{\s*expediteur_nom\s*\}\}/g,
        () => (expediteurNom ? varChip(escapeHtml(expediteurNom)) : ''))
      .replace(/\{\{\s*b[eé]n[eé]ficiaires?\s*\}\}/gi,
        () => (beneficiaires ? varChip(escapeHtml(beneficiaires)) : ''));
    // Signature (bloc HTML) : verrouillée, sauf si déjà présente dans le corps.
    const sigText = stripText(signature);
    const bodySansToken = stripText(out.replace(/\{\{\s*signature\s*\}\}/g, ''));
    const dejaPresente = sigText.length > 0 && bodySansToken.includes(sigText);
    out = out.replace(/\{\{\s*signature\s*\}\}/g, () => (dejaPresente ? '' :
      `<div class="msg-var" contenteditable="false" style="background:${T.accentSoft};` +
      `border-radius:6px;padding:2px 6px;">${signature}</div>`));
    // Variables encore non résolues ({{montant}}, …) : verrouillées telles quelles.
    out = out.replace(/\{\{\s*[a-z0-9_]+\s*\}\}/gi, m => varChip(m));
    return out;
  };

  // Retire les enveloppes de variables avant envoi/brouillon (garde le contenu).
  const unwrapVars = html => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html || '';
    tmp.querySelectorAll('.msg-var').forEach(el => {
      while (el.firstChild) el.parentNode.insertBefore(el.firstChild, el);
      el.remove();
    });
    return tmp.innerHTML;
  };

  const rootRef = useRef(null);
  const dragCleanupRef = useRef(null);
  const upd = patch => onUpdate(patch);

  useEffect(() => () => {
    if (dragCleanupRef.current) dragCleanupRef.current();
  }, []);

  function handleTitleMouseDown(e) {
    if (e.target.closest('button')) return;
    if (!rootRef.current) return;
    const rect = rootRef.current.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;

    function onMouseMove(ev) {
      let left = ev.clientX - offsetX;
      let top = ev.clientY - offsetY;
      // Largeur reelle mesuree dans le DOM (rect.width, capturee au mousedown
      // ci-dessus) : winW est desormais une expression CSS (min(760px, 90vw)),
      // non exploitable en arithmetique, et ne peut plus servir de proxy fiable
      // a la largeur affichee (cf. regression corrigee ici). rect.width refletant
      // ce que le navigateur a reellement calcule, elle reste correcte aussi bien
      // en mode normal qu'en mode agrandi, sans dupliquer la formule de winW.
      left = Math.min(Math.max(left, 0), Math.max(0, window.innerWidth - rect.width));
      // Borne verticale volontairement independante de la hauteur reelle : 40px
      // est la hauteur fixe de la barre de titre, pas une approximation de winH.
      // Le but est de garder la barre de titre accessible quand on glisse la
      // fenetre vers le bas, quelle que soit la hauteur de la fenetre. Inchangee.
      top = Math.min(Math.max(top, 0), window.innerHeight - 40);
      setPos({ left, top });
    }
    function onMouseUp() {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      dragCleanupRef.current = null;
    }
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    dragCleanupRef.current = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }

  function addTo(raw) {
    const val = raw.trim();
    if (!val) return;
    const m = val.match(/^(.+?)\s*<([^>]+)>$/);
    const name = m ? m[1].trim() : '';
    const email = m ? m[2] : val;
    if (!email.includes('@')) return;
    upd({ to: [...(win.to || []), { name, email }] });
    setToInput('');
  }

  function handleToKey(e) {
    if (['Enter', ','].includes(e.key)) { e.preventDefault(); addTo(toInput); }
  }

  async function loadTemplate(id) {
    if (!id) return;
    setLoadingTpl(true);
    try {
      // Backend attend : { templateTrackingId: UUID, sinistreTrackingId: UUID }
      const payload = {
        templateTrackingId: id,
        sinistreTrackingId: win.sinistreId || undefined,
      };
      const r = await axiosInstance.post('/v1/messagerie/templates/preview', payload);
      const d = r.data?.data || r.data;
      const preview = d?.corpsHtmlRendu || d?.corps || '';
      tplDirty.current = false; // nouveau modèle → ré-amorçage autorisé
      setTplPreview(preview);
      if (d?.sujetRendu) upd({ sujet: d.sujetRendu });
    } catch {
      setTplPreview(i18n.t('messagerie:compose.previsualisationImpossible'));
    } finally { setLoadingTpl(false); }
  }

  useEffect(() => { if (templateId) loadTemplate(templateId); }, [templateId, win.sinistre]);

  // Amorce/rafraîchit le corps éditable du modèle (résolu) tant que
  // l'utilisateur ne l'a pas adapté - puis on n'écrase plus ses modifications.
  useEffect(() => {
    if (tplDirty.current) return;
    setTplBody(seedTemplate(tplPreview));
  }, [tplPreview, signature, beneficiaires]); // eslint-disable-line react-hooks/exhaustive-deps

  async function saveDraft() {
    setSavingDraft(true);
    try {
      // Backend attend EnvoyerMailRequest JSON : destinataire, sujet, corpsHtml, nature, sinistreTrackingId
      const payload = {
        destinataire: (win.to || []).map(t => t.email).join(','),
        sujet: win.sujet || '',
        corpsHtml: tab === 'template' ? unwrapVars(tplBody) : resolve(win.body || ''),
        nature: win.nature || null,
        sinistreTrackingId: win.sinistreId || null,
      };
      await axiosInstance.post('/v1/messagerie/brouillons', payload);
      if (onDraft) onDraft();
    } finally { setSavingDraft(false); }
  }

  async function send() {
    // Onglet modèle : tplBody est déjà résolu + adapté ; on retire les
    // enveloppes de variables verrouillées avant l'envoi.
    const body = tab === 'template' ? unwrapVars(tplBody) : resolve(win.body || '');
    if (!(win.to?.length) || !body.trim()) return;
    setSending(true);
    try {
      if (win.attachments?.length) {
        // Avec pièces jointes : multipart - part "data" = JSON, part "fichiers" = fichiers
        const data = {
          destinataire: (win.to || []).map(t => t.email).join(','),
          sujet: win.sujet || '(Sans objet)',
          corpsHtml: body,
          nature: win.nature || null,
          sinistreTrackingId: win.sinistreId || null,
        };
        const fd = new FormData();
        fd.append('data', new Blob([JSON.stringify(data)], { type: 'application/json' }));
        win.attachments.forEach(f => fd.append('fichiers', f));
        await axiosInstance.post('/v1/messagerie/envoyer', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        // Sans pièce jointe : JSON simple
        await axiosInstance.post('/v1/messagerie/envoyer', {
          destinataire: (win.to || []).map(t => t.email).join(','),
          sujet: win.sujet || '(Sans objet)',
          corpsHtml: body,
          nature: win.nature || null,
          sinistreTrackingId: win.sinistreId || null,
        });
      }
      if (onSend) onSend(); else onClose();
    } catch {
      upd({ sendError: true });
    } finally { setSending(false); }
  }

  /* ── Fenêtre minimisée ── */
  if (win.minimized) {
    return (
      <div
        style={{
          position: 'fixed', bottom: 0, right: 24 + (minIndex || 0) * 252, zIndex: 3000,
          width: 240, height: 40, background: '#1f2937', color: '#fff',
          display: 'flex', alignItems: 'center', padding: '0 12px',
          borderRadius: '8px 8px 0 0', cursor: 'pointer',
          boxShadow: '0 -2px 8px rgba(0,0,0,.25)', userSelect: 'none',
          fontFamily: T.font,
        }}
        onClick={() => upd({ minimized: false })}
      >
        <span style={{ flex: 1, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {win.sujet || t('compose.nouveauMessage')}
        </span>
        <span style={{ fontSize: 12, opacity: .6, display: 'flex' }}><UpOutlined /></span>
        <button onClick={e => { e.stopPropagation(); onClose(); }} style={{ border: 'none', background: 'transparent', color: '#fff', cursor: 'pointer', padding: '0 0 0 8px', fontSize: 14, display: 'flex', alignItems: 'center' }}><CloseOutlined /></button>
      </div>
    );
  }

  // Taille par defaut agrandie : la zone de saisie etait a l'etroit. Les bornes en
  // vw/vh evitent le debordement sur un portable, ou 760px depasseraient la place
  // disponible une fois la sidebar deduite. Le mode agrandi reste inchange.
  const winW = win.maximized ? '80vw' : 'min(760px, 90vw)';
  const winH = win.maximized ? '80vh' : 'min(640px, 85vh)';

  const posStyle = win.maximized
    ? { position: 'fixed', left: '10vw', top: '5vh', zIndex: 3000 + (openIndex || 0) }
    : pos
      ? { position: 'fixed', left: pos.left, top: pos.top, zIndex: 3000 + (openIndex || 0) }
      // Pas d'empilement aligné sur la largeur par défaut réelle (winW = min(760px, 90vw) ;
      // 760 + 16px de battement, comme l'ancien 500 + 16 = 516 sur l'ex-autoW).
      : { position: 'fixed', right: 24 + (openIndex || 0) * 776, bottom: 0, zIndex: 3000 + (openIndex || 0) };

  return (
    <div ref={rootRef} style={{
      ...posStyle,
      width: winW, height: winH, background: '#fff',
      borderRadius: '10px 10px 0 0', overflow: 'hidden',
      boxShadow: '0 8px 40px rgba(0,0,0,.22)',
      display: 'flex', flexDirection: 'column',
      border: '1px solid #d1d5db',
      fontFamily: T.font,
    }}>
      {/* Barre de titre */}
      <div
        onMouseDown={handleTitleMouseDown}
        style={{
          background: '#1f2937', color: '#fff', padding: '0 12px',
          height: 40, display: 'flex', alignItems: 'center', gap: 8,
          flexShrink: 0, userSelect: 'none', cursor: 'move',
        }}>
        <span style={{ flex: 1, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {win.sujet || t('compose.nouveauMessage')}
        </span>
        <WinBtn icon={<MinusOutlined />} title={t('compose.reduire')} onClick={() => upd({ minimized: true })} />
        <WinBtn icon={win.maximized ? <CompressOutlined /> : <ExpandOutlined />} title={win.maximized ? t('compose.restaurer') : t('compose.agrandir')} onClick={() => upd({ maximized: !win.maximized })} />
        <WinBtn icon={<CloseOutlined />} title={t('compose.fermer')} onClick={() => onClose()} />
      </div>

      {/* Champs à / sujet */}
      <div style={{ borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
        {/* À */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', borderBottom: '1px solid #f0f0f0', flexWrap: 'wrap', gap: 4 }}>
          <span style={{ fontSize: 12.5, color: '#6b7280', minWidth: 20, flexShrink: 0 }}>{t('compose.a')}</span>
          {(win.to || []).map((t, i) => t ? (
            <ToChip key={i} name={t.name} email={t.email} onRemove={() => upd({ to: (win.to || []).filter((_, j) => j !== i) })} />
          ) : null)}
          <input
            value={toInput}
            onChange={e => setToInput(e.target.value)}
            onKeyDown={handleToKey}
            onBlur={() => addTo(toInput)}
            placeholder={t('compose.adressePlaceholder')}
            style={{
              border: 'none', outline: 'none', flex: 1, minWidth: 150,
              fontSize: 13, padding: '2px 4px', fontFamily: 'inherit',
            }}
          />
        </div>
        {/* Sujet */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', gap: 8 }}>
          <span style={{ fontSize: 12.5, color: '#6b7280', flexShrink: 0 }}>{t('compose.objet')}</span>
          <input
            value={win.sujet || ''}
            onChange={e => upd({ sujet: e.target.value })}
            placeholder={t('compose.objetPlaceholder')}
            style={{ border: 'none', outline: 'none', flex: 1, fontSize: 13, padding: '2px 4px', fontFamily: 'inherit' }}
          />
        </div>
      </div>

      {/* Bandeau BNCB (sinistre + nature) */}
      <div style={{ background: '#f0fdf4', borderBottom: '1px solid #dcfce7', padding: '8px 12px', display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
        <span style={{ fontSize: 12, color: '#15803d', fontWeight: 600, flexShrink: 0 }}>{t('compose.dossierBncb')}</span>
        <Select
          size="small"
          style={{ flex: 1 }}
          placeholder={t('compose.numeroSinistre')}
          allowClear
          showSearch
          popupMatchSelectWidth={false}
          value={win.sinistreId || undefined}
          onChange={(v, opt) => upd({ sinistreId: v, sinistre: opt?.label?.split(' ')[0] || v })}
          options={(sinistres || []).map(s => ({ value: s.value, label: s.label || s.numero }))}
          filterOption={(i, opt) => (opt?.label || '').toLowerCase().includes(i.toLowerCase())}
        />
        <Select
          size="small"
          style={{ flex: 1 }}
          placeholder={t('compose.nature')}
          allowClear
          popupMatchSelectWidth={false}
          value={win.nature || undefined}
          onChange={v => upd({ nature: v })}
          options={NATURE_OPTIONS.map(k => ({ value: k, label: NATURE_LABEL[k] }))}
        />
      </div>

      {/* Onglets libre / template */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
        {['libre', 'template'].map(onglet => (
          <button key={onglet} onClick={() => setTab(onglet)} style={{
            flex: 1, border: 'none', cursor: 'pointer', fontFamily: 'inherit',
            fontSize: 12.5, fontWeight: tab === onglet ? 600 : 400,
            color: tab === onglet ? T.green : '#6b7280',
            padding: '8px 0', background: 'transparent',
            borderBottom: tab === onglet ? `2px solid ${T.green}` : '2px solid transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}>
            {onglet === 'libre' ? <><EditOutlined /> {t('compose.redactionLibre')}</> : <><SnippetsOutlined /> {t('compose.modeleBncb')}</>}
          </button>
        ))}
      </div>

      {/* Zone de rédaction */}
      <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        {tab === 'libre' ? (
          <OssanEditor
            value={win.body || ''}
            onChange={html => upd({ body: html })}
            placeholder={t('compose.redigez')}
            minHeight={220}
          />
        ) : (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: '10px 12px', gap: 10 }}>
            <Select
              style={{ width: '100%' }}
              placeholder={t('compose.choisirModele')}
              popupMatchSelectWidth={false}
              value={templateId}
              onChange={v => { setTemplateId(v); loadTemplate(v); }}
              options={(templates || []).map(t => ({ value: t.trackingId, label: t.nom }))}
            />
            <div className="cb-scroll" style={{ flex: 1, overflowY: 'auto' }}>
              {loadingTpl ? (
                <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 40 }}>
                  <Spin indicator={<LoadingOutlined style={{ fontSize: 20, color: T.green }} spin />} />
                </div>
              ) : (
                tplPreview
                  ? <OssanEditor
                    value={tplBody}
                    onChange={html => { tplDirty.current = true; setTplBody(html); }}
                    placeholder={t('compose.adaptez')}
                    minHeight={200}
                  />
                  : <span style={{ color: '#9ca3af' }}>{t('compose.selectionnezModele')}</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Pied de fenêtre */}
      <div style={{ borderTop: '1px solid #e5e7eb', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, background: '#fafafa' }}>
        <ToolBtn icon={<PaperClipOutlined />} title={t('compose.pieceJointe')} onClick={() => {
          const inp = document.createElement('input'); inp.type = 'file'; inp.multiple = true;
          inp.onchange = () => upd({ attachments: [...(win.attachments || []), ...Array.from(inp.files)] });
          inp.click();
        }} />
        <div style={{ flex: 1 }} />
        <button
          onClick={saveDraft} disabled={savingDraft}
          style={{
            border: '1px solid #e5e7eb', background: '#fff', borderRadius: 7,
            padding: '6px 12px', cursor: 'pointer', fontSize: 12.5,
            fontFamily: 'inherit', color: '#374151',
            display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          {savingDraft ? t('compose.sauvegarde') : <><SaveOutlined /> {t('compose.brouillon')}</>}
        </button>
        <button
          onClick={send}
          disabled={sending || !(win.to?.length)}
          style={{
            background: T.accent, color: '#fff', border: 'none', borderRadius: 7,
            padding: '7px 18px', fontWeight: 700, fontSize: 13,
            cursor: 'pointer', fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', gap: 6,
            opacity: (sending || !(win.to?.length)) ? .5 : 1,
          }}
        >
          {sending && <Spin indicator={<LoadingOutlined style={{ fontSize: 13, color: '#fff' }} spin />} />}
          {t('compose.envoyer')} <SendOutlined />
        </button>
      </div>

      {/* Indicateur pièces jointes */}
      {win.attachments?.length > 0 && (
        <div style={{ borderTop: '1px solid #f0f0f0', padding: '6px 12px', display: 'flex', flexWrap: 'wrap', gap: 6, flexShrink: 0 }}>
          {win.attachments.map((f, i) => (
            <span key={i} style={{ background: '#f3f4f6', borderRadius: 4, padding: '2px 8px', fontSize: 12, display: 'flex', gap: 5, alignItems: 'center' }}>
              <PaperClipOutlined /> {f.name}
              <button onClick={() => upd({ attachments: (win.attachments || []).filter((_, j) => j !== i) })}
                style={{ border: 'none', background: 'transparent', color: '#6b7280', cursor: 'pointer', padding: 0, fontSize: 12, display: 'flex', alignItems: 'center' }}>
                <CloseOutlined />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function WinBtn({ icon, title, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick} title={title}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        border: 'none', background: hov ? 'rgba(255,255,255,.2)' : 'transparent',
        color: '#fff', width: 24, height: 24, borderRadius: 4,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', fontSize: 11,
      }}>
      {icon}
    </button>
  );
}

function ToolBtn({ icon, title, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick} title={title}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        border: 'none', background: hov ? '#e5e7eb' : 'transparent',
        width: 32, height: 32, borderRadius: 6, cursor: 'pointer',
        fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
      {icon}
    </button>
  );
}
